import { defineStore } from 'pinia'
import { BASE_CURRENCY } from '@/enums/Currency'
import { formatAmount } from '@/views/finance/credit-notes/format'
import { useToastStore } from '@/stores/toast'
import {
  getCreditNotes,
  getCreditNote,
  cancelCreditNote,
  syncCreditNotePaymentStatus,
  paySepa,
  exportSepa,
  exportDatev,
  previewDraftCcn,
  createCcnFromCancelled,
  sendCreditNoteToLinguist,
  getCreditNoteAuditLog,
  getCcnLinguistDefaults,
  getEligibleJobsForCcnLinguist
} from '@/services/entities/credit-note'

type CreditNotesStore = {
  items: CcnListItem[]
  totalItems: number
  loading: boolean
  currentCcn: CcnEntity | null
  currentCcnLoading: boolean
  actionLoading: boolean
  auditLog: CcnAuditLogEntry[]
  auditLogLoading: boolean
}

const handleError = (error: unknown, toastStore: ReturnType<typeof useToastStore>): void => {
  const detail =
    typeof error === 'object' && error !== null
      ? (error as { _error?: string; message?: string })._error ??
        (error as { message?: string }).message ??
        'An error occurred.'
      : 'An error occurred.'
  toastStore.add({ severity: 'error', summary: 'Error', detail })
}

// Surface hydra:violations from a 422 response so the composer can render
// inline per-field error messages. Returns undefined when the error isn't a
// validation failure with violations.
const extractViolations = (error: unknown): CcnComposerViolation[] | undefined => {
  const response = (
    error as { response?: { status?: number; data?: { violations?: CcnComposerViolation[] } } }
  ).response
  if (!response || response.status !== 422) return undefined
  return response.data?.violations
}

const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  // Defer revocation so Firefox/Safari have a chance to start the download
  // before the blob URL becomes invalid.
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

export const useCreditNotesStore = defineStore('credit-notes', {
  state: (): CreditNotesStore => ({
    items: [],
    totalItems: 0,
    loading: false,
    currentCcn: null,
    currentCcnLoading: false,
    actionLoading: false,
    auditLog: [],
    auditLogLoading: false
  }),

  actions: {
    async fetchItems(
      page: number,
      itemsPerPage: number,
      filters: Record<string, string | string[] | number | boolean | null> = {},
      sort: Record<string, 'ASC' | 'DESC'> | null = null,
      groups: string[] = ['ccn:list', 'user:list', 'cancelable:read', 'timestampable'],
      linguistId?: string
    ) {
      const filterParams: any[] = Object.entries(filters)
        .filter(([, v]) => v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0))
        .flatMap(([key, value]) =>
          Array.isArray(value) ? value.map((v) => ({ [key]: v })) : [{ [key]: value }]
        )

      const params: any[] = [
        { page, itemsPerPage },
        ...groups.map((group) => ({ 'groups[]': group })),
        ...filterParams
      ]

      // CCN's OrderFilter uses orderParameterName=orderBy (vs. the default
      // `order` used elsewhere), so we emit `orderBy[field]=DIR` directly.
      if (sort) {
        const sortParam: Record<string, string> = {}
        Object.keys(sort).forEach((key) => {
          sortParam[`orderBy[${key}]`] = sort[key]
        })
        params.push(sortParam)
      }

      this.loading = true
      try {
        const response = await getCreditNotes(params, linguistId)
        this.items = response['hydra:member'] || []
        this.totalItems = response['hydra:totalItems']
      } catch (error) {
        this.items = []
        this.totalItems = 0
        throw error
      } finally {
        this.loading = false
      }
    },

    async fetchCcn(
      id: string,
      groups: string[] = [
        'ccn:read',
        'cancelable:read',
        'user:read',
        'payment-data:read',
        'cart:list',
        'order:list',
        'job:list',
        'job:ccn-composer',
        'timestampable'
      ]
    ) {
      this.currentCcnLoading = true
      try {
        const params = groups.map((g) => ({ 'groups[]': g }))
        this.currentCcn = await getCreditNote(id, params)
      } catch (error) {
        this.currentCcn = null
        throw error
      } finally {
        this.currentCcnLoading = false
      }
    },

    async cancel(id: string, cancellationDate: Date): Promise<void> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        await cancelCreditNote(id, cancellationDate)
        toastStore.success('Credit note cancelled.')
      } catch (error) {
        handleError(error, toastStore)
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async syncPaymentStatus(id: string): Promise<void> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        await syncCreditNotePaymentStatus(id)
        toastStore.success('Payment status synced.')
      } catch (error) {
        handleError(error, toastStore)
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async sendToLinguist(id: string): Promise<void> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        await sendCreditNoteToLinguist(id)
        toastStore.success('Credit note sent to linguist.')
      } catch (error) {
        handleError(error, toastStore)
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async paySepa(ids: string[]): Promise<void> {
      if (ids.length === 0) return
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        await paySepa(ids)
        toastStore.success('SEPA payments marked as paid.')
      } catch (error) {
        handleError(error, toastStore)
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async exportSepa(ids: string[]): Promise<void> {
      if (ids.length === 0) return
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        const blob = await exportSepa(ids)
        triggerBlobDownload(blob, 'Ccns_payment_method_bank_transfer.xml')
        toastStore.success('SEPA export generated.')
      } catch (error) {
        handleError(error, toastStore)
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async exportDatev(from: Date, to: Date): Promise<void> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        const { blob, filename } = await exportDatev(from, to)
        triggerBlobDownload(blob, filename)
        toastStore.success('Datev export generated.')
      } catch (error) {
        handleError(error, toastStore)
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async previewDraftPdf(cancelledCcnId: string, payload: CcnDraftPayload): Promise<void> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        const blob = await previewDraftCcn(cancelledCcnId, payload)
        triggerBlobDownload(blob, `ccn-draft-${cancelledCcnId.slice(0, 8)}.pdf`)
      } catch (error) {
        handleError(error, toastStore)
        const violations = extractViolations(error)
        if (violations?.length) {
          const enhanced: CcnComposerError = new Error('Validation failed')
          enhanced.violations = violations
          throw enhanced
        }
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async createFromCancelled(
      cancelledCcnId: string,
      payload: CcnDraftPayload
    ): Promise<{ id: string; receiptNumber: string; sent: boolean }> {
      const toastStore = useToastStore()
      this.actionLoading = true
      try {
        const result = await createCcnFromCancelled(cancelledCcnId, payload)
        const payout =
          result.payoutCurrency && result.payoutCurrency !== BASE_CURRENCY && result.payoutFinalFee
            ? ` (${formatAmount(result.payoutFinalFee, result.payoutCurrency)})`
            : ''
        toastStore.success(
          `New credit note ${result.receiptNumber}${payout} created${result.sent ? ' and sent to the linguist' : ''}.`
        )
        return { id: result.id, receiptNumber: result.receiptNumber, sent: result.sent }
      } catch (error) {
        handleError(error, toastStore)
        const violations = extractViolations(error)
        if (violations?.length) {
          const enhanced: CcnComposerError = new Error('Validation failed')
          enhanced.violations = violations
          throw enhanced
        }
        throw error
      } finally {
        this.actionLoading = false
      }
    },

    async fetchAuditLog(id: string): Promise<void> {
      this.auditLogLoading = true
      try {
        this.auditLog = await getCreditNoteAuditLog(id)
      } catch (error) {
        this.auditLog = []
        throw error
      } finally {
        this.auditLogLoading = false
      }
    },

    async fetchEligibleJobs(linguistId: string, currentCcnSource?: string): Promise<unknown[]> {
      // Returns the raw hydra members — the composer form decides which fields
      // to surface in its combobox. `currentCcnSource` opts into the
      // cross-cancelled-CCN re-composition flow (#1829): when set, the api
      // also returns jobs currently linked to OTHER cancelled CCNs of the
      // same linguist.
      return await getEligibleJobsForCcnLinguist(linguistId, currentCcnSource)
    },

    async fetchCcnLinguistDefaults(ccnId: string): Promise<CcnLinguistDefaults | null> {
      // Loaded on top of `fetchCcn` because MaxDepth(1) on Ccn.linguist hides
      // the linguist's billingAddress + paymentData relations in the main
      // detail response. Powers the composer's snapshot pre-fill AND the
      // VAT-eligibility-driven gross-fee auto-calculation.
      try {
        return await getCcnLinguistDefaults(ccnId)
      } catch {
        return null
      }
    }
  }
})
